import { supabase } from "../supabase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { User, Warning } from "@shiftly/shared/src/schema";
import { DateTime } from "luxon";
import SupabaseNotificationService from "../notifications/supabaseNotificationService";
import SupabaseUserLoggingService from "../logging/supabaseUserLoggingService";

export interface SupabaseUser {
  id: string;
  full_name: string;
  email: string;
  fansmetric_email?: string;
  pay_class?: "UK" | "FP";
  date_of_birth?: string;
  verified: boolean;
  verified_by?: string;
  verified_at?: string;
  created_at: string;
  updated_at: string;
  discord?: {
    id: string;
    username: string;
    avatar: string | null;
    linked_at: string;
  };
  privacy_policy_accepted?: {
    acceptedAt: string;
    version: string;
  };
  terms_accepted?: {
    acceptedAt: string;
    version: string;
  };
  tax?: {
    confirmedAt: string;
    country: string;
  };
  roles: string[];
  deactivated_at?: DateTime;
}

export interface SupabaseWarning {
  id: string;
  user_id: string;
  message: string;
  issued_by: string;
  issued_at: string;
  created_at: string;
}

const SupabaseUserService = {
  async getUsers(): Promise<User[]> {
    try {
      const { data: users_data, error: users_error } = await supabase
        .from("v_users_with_roles")
        .select("*");

      if (users_error) throw users_error;

      const { data: warnings_data, error: warnings_error } = await supabase
        .from("warnings")
        .select("*");

      if (warnings_error) throw warnings_error;

      const warnings_by_user = warnings_data?.reduce((acc, warning) => {
        if (!acc[warning.user_id]) acc[warning.user_id] = [];
        acc[warning.user_id].push({
          id: warning.id,
          message: warning.message,
          issued_by: warning.issued_by,
          issued_at: DateTime.fromISO(warning.issued_at, { zone: 'UTC' }),
        });
        return acc;
      }, {} as Record<string, Warning[]>) || {};

      return (users_data || []).map((user) => ({
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        fansmetric_email: user.fansmetric_email,
        pay_class: user.pay_class,
        date_of_birth: user.date_of_birth
          ? DateTime.fromISO(user.date_of_birth, { zone: 'UTC' })
          : undefined,
        verified: user.verified,
        verified_by: user.verified_by,
        verified_at: user.verified_at
          ? DateTime.fromISO(user.verified_at, { zone: 'UTC' })
          : DateTime.utc(),
        roles: user.roles || [],
        warnings: warnings_by_user[user.id] || [],
        created_at: DateTime.fromISO(user.created_at, { zone: 'UTC' }),
        updated_at: DateTime.fromISO(user.updated_at, { zone: 'UTC' }),
        deactivated_at: user.deactivated_at
          ? DateTime.fromISO(user.deactivated_at, { zone: 'UTC' })
          : undefined,
        discord: user.discord
          ? {
            ...user.discord,
            linked_at: DateTime.fromISO(user.discord.linked_at, { zone: 'UTC' }),
            expires_at: DateTime.fromISO(user.discord.expires_at, { zone: 'UTC' }),
          }
          : undefined,
        terms_accepted: user.terms_accepted
          ? {
            ...user.terms_accepted,
            accepted_at: DateTime.fromISO(user.terms_accepted.accepted_at, { zone: 'UTC' }),
          }
          : undefined,
        privacy_policy_accepted: user.privacy_policy_accepted
          ? {
            ...user.privacy_policy_accepted,
            accepted_at: DateTime.fromISO(user.privacy_policy_accepted.accepted_at, { zone: 'UTC' }),
          }
          : undefined,
        tax: user.tax
          ? {
            ...user.tax,
            confirmed_at: DateTime.fromISO(user.tax.confirmed_at, { zone: 'UTC' }),
          }
          : undefined,
        start_date: DateTime.fromISO(user.created_at, { zone: 'UTC' }),
        notification_subscriptions: [],
      }));
    } catch (error) {
      console.error("Error getting users:", error);
      return [];
    }
  },

  async getUser(id: string): Promise<User | null> {
    try {
      const { data: user_data, error: user_error } = await supabase
        .from("v_users_with_roles")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (user_error) throw user_error;
      if (!user_data) return null;

      const { data: warnings_data, error: warnings_error } = await supabase
        .from("warnings")
        .select("*")
        .eq("user_id", id);

      if (warnings_error) throw warnings_error;

      const warnings: Warning[] = (warnings_data || []).map((warning) => ({
        id: warning.id,
        message: warning.message,
        issued_by: warning.issued_by,
        issued_at: DateTime.fromISO(warning.issued_at, { zone: 'UTC' }),
      }));

      return {
        id: user_data.id,
        full_name: user_data.full_name,
        email: user_data.email,
        fansmetric_email: user_data.fansmetric_email,
        pay_class: user_data.pay_class,
        date_of_birth: user_data.date_of_birth
          ? DateTime.fromISO(user_data.date_of_birth, { zone: 'UTC' })
          : undefined,
        verified: user_data.verified,
        verified_by: user_data.verified_by,
        verified_at: user_data.verified_at
          ? DateTime.fromISO(user_data.verified_at, { zone: 'UTC' })
          : DateTime.utc(),
        roles: user_data.roles || [],
        warnings,
        created_at: DateTime.fromISO(user_data.created_at, { zone: 'UTC' }),
        updated_at: DateTime.fromISO(user_data.updated_at, { zone: 'UTC' }),
        deactivated_at: user_data.deactivated_at
          ? DateTime.fromISO(user_data.deactivated_at, { zone: 'UTC' })
          : undefined,
        discord: user_data.discord
          ? {
            ...user_data.discord,
            linked_at: DateTime.fromISO(user_data.discord.linked_at, { zone: 'UTC' }),
            expires_at: DateTime.fromISO(user_data.discord.expires_at, { zone: 'UTC' }),
          }
          : undefined,
        terms_accepted: user_data.terms_accepted
          ? {
            ...user_data.terms_accepted,
            accepted_at: DateTime.fromISO(user_data.terms_accepted.acceptedAt, { zone: 'UTC' }),
          }
          : undefined,
        privacy_policy_accepted: user_data.privacy_policy_accepted
          ? {
            ...user_data.privacy_policy_accepted,
            accepted_at: DateTime.fromISO(
              user_data.privacy_policy_accepted.acceptedAt,
              { zone: 'UTC' }
            ),
          }
          : undefined,
        tax: user_data.tax
          ? {
            ...user_data.tax,
            confirmed_at: DateTime.fromISO(user_data.tax.confirmedAt, { zone: 'UTC' }),
          }
          : undefined,
        start_date: DateTime.fromISO(user_data.created_at, { zone: 'UTC' }),
        notification_subscriptions: [],
      } as User;
    } catch (error) {
      console.error("Error getting user:", error);
      return null;
    }
  },

  async createUserManually(
    user: Pick<User, "full_name" | "pay_class" | "roles">,
  ): Promise<string | null> {
    const { data, error } = await supabase.functions.invoke(
      "user-create-manual",
      {
        method: "POST",
        body: JSON.stringify({
          full_name: user.full_name,
          pay_class: user.pay_class,
          roles: user.roles || [],
        }),
      },
    );

    if (error) throw error;
    if (!data || !data.user_id) throw new Error("No user ID returned");

    // Log the user creation
    try {
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      if (current_user) {
        // Fetch the created user for logging
        const { data: created_user } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.user_id)
          .single();
        
        if (created_user) {
          await SupabaseUserLoggingService.logUserCreate(created_user, current_user);
        }
      }
    } catch (logError) {
      console.error('Error logging user creation:', logError);
    }

    return null
  },

  async promoteTrial(
    user_id: string,
    email: string,
    roles: string[],
  ): Promise<void> {
    // Get current user for logging
    const current_user = (await supabase.auth.getUser()).data.user?.id;
    
    // Get the user data for logging
    let user_to_promote: User | undefined;
    if (current_user) {
      try {
        user_to_promote = await this.getUser(user_id) || undefined;
      } catch (error) {
        console.error('Error fetching user for logging:', error);
      }
    }

    const { data, error } = await supabase.functions.invoke("user-promote-trial", {
      method: "POST",
      body: JSON.stringify({
        user_id: user_id,
        new_email: email,
        roles: roles,
      }),
    });

    if (error) throw error;
    if (!data || !data.user_id) throw new Error("No user ID returned");

    // Log the user promotion
    if (current_user && user_to_promote) {
      try {
        await SupabaseUserLoggingService.logUserPromoteTrial(user_to_promote, email, roles, current_user);
      } catch (logError) {
        console.error('Error logging user promotion:', logError);
        // Don't throw here - the user was promoted successfully
      }
    }
  },

  async inviteUserByEmail(
    emails: string[],
    roles: string[],
    pay_classes: string
  ): Promise<void> {
    // Get current user for logging
    const current_user = (await supabase.auth.getUser()).data.user?.id;

    const { data, error } = await supabase.functions.invoke("user-invite-by-email",
      {
        method: "POST",
        body: JSON.stringify({
          emails: emails,
          roles: roles,
          pay_classes: pay_classes,
        }),
      });
    if (error) throw error;
    if (!data || !data.results) throw new Error("No emails provided");

    // Log the user invitation
    if (current_user) {
      try {
        await SupabaseUserLoggingService.logUserInvite(emails, roles, [pay_classes], current_user);
      } catch (logError) {
        console.error('Error logging user invitation:', logError);
        // Don't throw here - the invitation was sent successfully
      }
    }
  },

  async updateUser(id: string, user: Partial<User>): Promise<void> {
    try {
      // Get current user for logging
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current user data for logging
      let before_user: User | undefined;
      if (current_user) {
        try {
          before_user = await this.getUser(id) || undefined;
        } catch (error) {
          console.error('Error fetching user for logging:', error);
        }
      }

      const update_data: Partial<SupabaseUser> = {
        updated_at: DateTime.utc().toISO(),
      };

      if (user.full_name !== undefined) update_data.full_name = user.full_name;
      if (user.email !== undefined) update_data.email = user.email;
      if (user.fansmetric_email !== undefined) {
        update_data.fansmetric_email = user.fansmetric_email;
      }
      if (user.pay_class !== undefined) update_data.pay_class = user.pay_class;
      if (user.date_of_birth !== undefined) {
        update_data.date_of_birth = user.date_of_birth?.toISO() || undefined;
      }
      if (user.verified !== undefined) update_data.verified = user.verified;
      if (user.verified_by !== undefined) {
        update_data.verified_by = user.verified_by;
      }
      if (user.verified_at !== undefined) {
        update_data.verified_at = user.verified_at.toISO()!;
      }
      if (!user.deactivated_at) {
        update_data.deactivated_at = user.deactivated_at;
      }

      if (user.discord !== undefined) {
        update_data.discord = user.discord
          ? {
            ...user.discord,
            linked_at: user.discord.linked_at.toISO()!,
          }
          : undefined;
      }

      if (user.terms_accepted !== undefined) {
        update_data.terms_accepted = user.terms_accepted
          ? {
            ...user.terms_accepted,
            acceptedAt: user.terms_accepted.accepted_at.toISO()!,
          }
          : undefined;
      }

      if (user.privacy_policy_accepted !== undefined) {
        update_data.privacy_policy_accepted = user.privacy_policy_accepted
          ? {
            ...user.privacy_policy_accepted,
            acceptedAt: user.privacy_policy_accepted.accepted_at.toISO()!,
          }
          : undefined;
      }

      if (user.tax !== undefined) {
        update_data.tax = user.tax
          ? {
            ...user.tax,
            confirmedAt: user.tax.confirmed_at.toISO()!,
          }
          : undefined;
      }

      const { error: update_error } = await supabase
        .from("users")
        .update(update_data)
        .eq("id", id);

      if (update_error) throw update_error;

      if (user.roles !== undefined) {
        const { error: role_update } = await supabase
          .rpc('update_user_roles', { p_user_id: id, p_role_ids: user.roles });

        if (role_update) throw role_update;
      }

      // Log the user update
      if (current_user && before_user) {
        try {
          const after_user = await this.getUser(id);
          if (after_user) {
            await SupabaseUserLoggingService.logUserUpdate(id, {
              before: before_user,
              after: after_user
            }, current_user);
          }
        } catch (logError) {
          console.error('Error logging user update:', logError);
          // Don't throw here - the user was updated successfully
        }
      }

    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  async addWarning(
    user_id: string,
    warning: Omit<Warning, "id" | "issued_at">,
    send_notification: boolean = false,
  ): Promise<Warning | undefined> {
    try {
      // Get current user for logging
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      
      // Get the user data for logging
      let user_for_warning: User | undefined;
      if (current_user) {
        try {
          user_for_warning = await this.getUser(user_id) || undefined;
        } catch (error) {
          console.error('Error fetching user for logging:', error);
        }
      }

      const now = DateTime.utc().toISO();
      const new_warnings = {
        user_id: user_id,
        message: warning.message,
        issued_by: warning.issued_by,
        issued_at: now,
        created_at: now,
      };

      const { data, error } = await supabase
        .from("warnings")
        .insert([new_warnings])
        .select()
        .single();

      if (error) throw error;

      const created_warning = {
        id: data.id,
        message: data.message,
        issued_by: data.issued_by,
        issued_at: DateTime.fromISO(data.issued_at, { zone: 'UTC' }),
      };

      // Log the warning addition
      if (current_user && user_for_warning) {
        try {
          await SupabaseUserLoggingService.logUserAddWarning(user_for_warning, created_warning, current_user);
        } catch (logError) {
          console.error('Error logging warning addition:', logError);
          // Don't throw here - the warning was added successfully
        }
      }

      if (send_notification) {
        try {
          SupabaseNotificationService.createNotification({
            title: "You have received a warning",
            message: `You have received a warning. ${warning.message}`,
            type: "WARNING",
            group: "SYSTEM",
            target_users: [user_id],
            created_by: "system",
          });
        } catch (notification_error) {
          console.error("Error sending notification:", notification_error);
        }
      }

      return created_warning;
    } catch (error) {
      console.error("Error adding warning:", error);
      throw error;
    }
  },

  async removeWarning(user_id: string, warning_id: string): Promise<void> {
    try {
      // Get current user for logging
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      
      // Get the user and warning data for logging
      let user_for_warning: User | undefined;
      let warning_to_remove: Warning | undefined;
      if (current_user) {
        try {
          user_for_warning = await this.getUser(user_id) || undefined;
          
          // Get the warning data before deletion
          const { data: warning_data } = await supabase
            .from("warnings")
            .select("*")
            .eq("id", warning_id)
            .eq("user_id", user_id)
            .single();
          
          if (warning_data) {
            warning_to_remove = {
              id: warning_data.id,
              message: warning_data.message,
              issued_by: warning_data.issued_by,
              issued_at: DateTime.fromISO(warning_data.issued_at, { zone: 'UTC' }),
            };
          }
        } catch (error) {
          console.error('Error fetching data for logging:', error);
        }
      }

      const { error } = await supabase
        .from("warnings")
        .delete()
        .eq("id", warning_id)
        .eq("user_id", user_id);

      if (error) throw error;

      // Log the warning removal
      if (current_user && user_for_warning && warning_to_remove) {
        try {
          await SupabaseUserLoggingService.logUserRemoveWarning(user_for_warning, warning_to_remove, current_user);
        } catch (logError) {
          console.error('Error logging warning removal:', logError);
          // Don't throw here - the warning was removed successfully
        }
      }
    } catch (error) {
      console.error("Error removing warning:", error);
      throw error;
    }
  },

  async deleteUser(user_id: string | { user_id: string }, target_user_id?: string): Promise<void> {
    try {
      const actual_user_id = target_user_id || (typeof user_id === 'string' ? user_id : user_id.user_id);

      // Get current user for logging
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      
      // Get the user data for logging before deletion
      let user_to_delete: User | undefined;
      if (current_user) {
        try {
          user_to_delete = await this.getUser(actual_user_id) || undefined;
        } catch (error) {
          console.error('Error fetching user for logging:', error);
        }
      }

      const { data, error } = await supabase.functions.invoke("user-delete", {
        body: JSON.stringify({
          user_id: actual_user_id,
        }),
      });

      if (error) throw error;

      // Log the user deletion
      if (current_user && user_to_delete) {
        try {
          await SupabaseUserLoggingService.logUserDelete(user_to_delete, current_user);
        } catch (logError) {
          console.error('Error logging user deletion:', logError);
          // Don't throw here - the user was deleted successfully
        }
      }

      console.log("User deleted:", data?.deleted_user_id);
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  },

  async deactivateUser(user_id: string): Promise<void> {
    try {
      // Get current user for logging
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      
      // Get the user data for logging before deactivation
      let user_to_deactivate: User | undefined;
      if (current_user) {
        try {
          user_to_deactivate = await this.getUser(user_id) || undefined;
        } catch (error) {
          console.error('Error fetching user for logging:', error);
        }
      }

      const { data, error } = await supabase.functions.invoke("user-deactivate", {
        body: JSON.stringify({
          user_id: user_id,
        }),
      });

      if (error) throw error;

      // Log the user deactivation
      if (current_user && user_to_deactivate) {
        try {
          await SupabaseUserLoggingService.logUserDeactivate(user_to_deactivate, current_user);
        } catch (logError) {
          console.error('Error logging user deactivation:', logError);
          // Don't throw here - the user was deactivated successfully
        }
      }

      console.log("User deactivated:", data?.deactivated_user_id);
    } catch (error) {
      console.error("Error deactivating user:", error);
      throw error;
    }
  },

  async deleteTransferUser(user_id: string, target_user_id: string): Promise<void> {
    try {
      const { data, error } = await supabase.functions.invoke("user-delete", {
        body: JSON.stringify({
          user_id: user_id,
          target_user_id: target_user_id,
        }),
      });

      if (error) throw error;
      console.log(`Deleted user ${data?.deleted_user_id} to ${target_user_id}:`);
    } catch (error) {
      console.error(`Error deleting user:`, error);
      throw error;
    }
  },


  async reactivateUser(user_id: string): Promise<void> {
    try {
      const current_user = (await supabase.auth.getUser()).data.user?.id;
      
      let user_to_reactivate: User | undefined;
      if (current_user) {
        try {
          user_to_reactivate = await this.getUser(user_id) || undefined;
        } catch (error) {
          console.error('Error fetching user for logging:', error);
        }
      }

      const { data, error } = await supabase.functions.invoke("user-reactivate", {
        body: JSON.stringify({
          user_id: user_id,
        }),
      });

      if (error) throw error;

      // Log the user reactivation
      if (current_user && user_to_reactivate) {
        try {
          await SupabaseUserLoggingService.logUserReactivate(user_to_reactivate, current_user);
        } catch (logError) {
          console.error('Error logging user reactivation:', logError);
        }
      }

      console.log("User reactivated:", data?.reactivated_user_id);
    } catch (error) {
      console.error("Error reactivating user:", error);
      throw error;
    }
  },

  async sendPasswordReset(email: string): Promise<void> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { 
        redirectTo: `${window.location.origin}/password-reset` 
      });
      if (error) throw error;
    } catch (error) {
      console.error("Error sending password reset email:", error);
      throw error;
    }
  },

  async sendMagicLink(user_id: string): Promise<void> {
    try {
      const { data, error } = await supabase.functions.invoke("user-send-magic-link", {
        body: JSON.stringify({
          user_id: user_id,
        }),
      });

      if (error) throw error;
      console.log("Magic link sent:", data?.user_id);
    } catch (error) {
      console.error("Error sending magic link:", error);
      throw error;
    }
  },

  async getDiscordUserInfo(access_token: string): Promise<{ username: string; avatar: string | null } | null> {
    try {
      const response = await fetch('https://discord.com/api/users/@me', {
        headers: {
          'Authorization': `Bearer ${access_token}`,
        },
      });

      if (!response.ok) {
        console.warn('Failed to fetch Discord user info:', response.status);
        return null;
      }

      const discord_user = await response.json();
      return {
        username: discord_user.username,
        avatar: discord_user.avatar,
      };
    } catch (error) {
      console.error('Error fetching Discord user info:', error);
      return null;
    }
  },

  async getUserRoles(
    user_id: string,
  ): Promise<{ id: string; name: string; permissions: string[] }[]> {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select(`
          roles (
            id,
            name,
            role_permissions (
              permissions (
                key
              )
            )
          )
        `)
        .eq("user_id", user_id);

      if (error) throw error;

      return (data || []).map((user_role: any) => ({
        id: user_role.roles.id,
        name: user_role.roles.name,
        permissions: user_role.roles.role_permissions.map((rp: any) =>
          rp.permissions.key
        ),
      }));
    } catch (error) {
      console.error("Error getting user roles:", error);
      return [];
    }
  },

  async assignRoleToUser(user_id: string, role_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from("user_roles")
        .insert([{ user_id: user_id, role_id: role_id }]);

      if (error) throw error;
    } catch (error) {
      console.error("Error assigning role to user:", error);
      throw error;
    }
  },

  async removeRoleFromUser(user_id: string, role_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", user_id)
        .eq("role_id", role_id);

      if (error) throw error;
    } catch (error) {
      console.error("Error removing role from user:", error);
      throw error;
    }
  },
};

export const useUsers = (user_id?: string) => {
  return useQuery({
    queryKey: ["users", user_id],
    queryFn: async () => {
      console.log("[useUsers] Fetching users with user_id:", user_id);

      try {
        if (user_id) {
          console.log("[useUsers] Fetching single user");
          return await SupabaseUserService.getUser(user_id);
        }

        console.log("[useUsers] Fetching all users");
        return await SupabaseUserService.getUsers();
      } catch (error) {
        console.error("[useUsers] Error fetching users:", error);
        throw error;
      }
    },
    select: (data) => {
      return Array.isArray(data) ? data : data ? [data] : [];
    },
    retry: (failure_count, error) => {
      if (error instanceof Error && error.message.includes("permission")) {
        return false;
      }
      return failure_count < 3;
    },
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ["users", id],
    queryFn: async () => {
      if (!id) return null;

      const user_data = await SupabaseUserService.getUser(id);
      if (!user_data) return null;

      const role_permissions = await SupabaseUserService.getUserRoles(id);

      return {
        ...user_data,
        role_permissions: role_permissions.flatMap((role) => role.permissions),
      } as User;
    },
    enabled: !!id,
    retry: (failure_count, error) => {
      if (error instanceof Error && error.message.includes("permission")) {
        return false;
      }
      return failure_count < 3;
    },
  });
};

export const useCreateUser = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: ({ user }: {
      user:
      | Omit<User, "created_at" | "updated_at">
      | Pick<User, "full_name" | "pay_class" | "roles">;
    }) => {
      return SupabaseUserService.createUserManually(
        user as Pick<User, "full_name" | "pay_class" | "roles">,
      );
    },
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useUpdateUser = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, user }: { id: string; user: Partial<User> }) =>
      SupabaseUserService.updateUser(id, user),
    onSuccess: (_, { id }) => {
      query_client.invalidateQueries({ queryKey: ["users"] });
      query_client.invalidateQueries({ queryKey: ["users", id] });
    },
    onMutate: async ({ id, user }) => {
      await query_client.cancelQueries({ queryKey: ["users", id] });
      const previous_user = query_client.getQueryData(["users", id]);

      query_client.setQueryData(["users", id], (old: any) => ({
        ...old,
        ...user,
      }));

      return { previous_user: previous_user };
    },
    onError: (__, _, context: any) => {
      if (context?.previousUser) {
        query_client.setQueryData(
          ["users", context.previousUser.id],
          context.previousUser,
        );
      }
    },
  });
};

export const useDeleteUser = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      user_id: string;
      target_user_id?: string;
    }) => SupabaseUserService.deleteUser(params),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
      query_client.invalidateQueries({ queryKey: ["shifts"] });
    },
  });
};

export const useAddWarning = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: ({ user_id, warning, send_notification }: {
      user_id: string;
      warning: Omit<Warning, "id" | "issued_at">;
      send_notification?: boolean;
    }) => SupabaseUserService.addWarning(user_id, warning, send_notification),
    onSuccess: (_, { user_id }) => {
      query_client.invalidateQueries({ queryKey: ["users"] });
      query_client.invalidateQueries({ queryKey: ["users", user_id] });
    },
  });
};

export const useRemoveWarning = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (
      { user_id, warning_id }: { user_id: string; warning_id: string },
    ) => SupabaseUserService.removeWarning(user_id, warning_id),
    onSuccess: (_, { user_id }) => {
      query_client.invalidateQueries({ queryKey: ["users"] });
      query_client.invalidateQueries({ queryKey: ["users", user_id] });
    },
  });
};

export const useDeactivateUser = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (user_id: string) => SupabaseUserService.deactivateUser(user_id),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useReactivateUser = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (user_id: string) => SupabaseUserService.reactivateUser(user_id),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useSendPasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => SupabaseUserService.sendPasswordReset(email),
  });
};

export const useSendMagicLink = () => {
  return useMutation({
    mutationFn: (user_id: string) => SupabaseUserService.sendMagicLink(user_id),
  });
};

export const useUserRoles = (user_id: string) => {
  return useQuery({
    queryKey: ["userRoles", user_id],
    queryFn: () => SupabaseUserService.getUserRoles(user_id),
    enabled: !!user_id,
  });
};

export const useDiscordUserInfo = (discord_id: string | undefined, access_token: string | undefined) => {
  return useQuery({
    queryKey: ["discordUserInfo", discord_id],
    queryFn: () => {
      if (!access_token) return null;
      return SupabaseUserService.getDiscordUserInfo(access_token);
    },
    enabled: !!discord_id && !!access_token,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: false, // Don't retry if Discord API fails
  });
};

export const useAssignRole = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: ({ user_id, role_id }: { user_id: string; role_id: string }) =>
      SupabaseUserService.assignRoleToUser(user_id, role_id),
    onSuccess: (_, { user_id }) => {
      query_client.invalidateQueries({ queryKey: ["users"] });
      query_client.invalidateQueries({ queryKey: ["users", user_id] });
      query_client.invalidateQueries({ queryKey: ["userRoles", user_id] });
    },
  });
};

export const useRemoveRole = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: ({ user_id, role_id }: { user_id: string; role_id: string }) =>
      SupabaseUserService.removeRoleFromUser(user_id, role_id),
    onSuccess: (_, { user_id }) => {
      query_client.invalidateQueries({ queryKey: ["users"] });
      query_client.invalidateQueries({ queryKey: ["users", user_id] });
      query_client.invalidateQueries({ queryKey: ["userRoles", user_id] });
    },
  });
};

export const usePromoteTrial = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (
      { user_id, email, roles }: {
        user_id: string;
        email: string;
        roles: string[];
      },
    ) => SupabaseUserService.promoteTrial(user_id, email, roles),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useInviteUserByEmail = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (
      { emails, roles, pay_classes }: {
        emails: string[];
        roles: string[];
        pay_classes: string;
      },
    ) => SupabaseUserService.inviteUserByEmail(emails, roles, pay_classes),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useDeleteTransferUser = () => {
  const query_client = useQueryClient();
  return useMutation({
    mutationFn: (
      { user_id, target_user_id }: {
        user_id: string;
        target_user_id: string
      }) =>
      SupabaseUserService.deleteTransferUser(user_id, target_user_id),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export default SupabaseUserService;
